import { ButtonHTMLAttributes, forwardRef } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, children, className = '', disabled, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center font-semibold rounded-button transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'

    const variants = {
      primary: 'bg-primary-600 text-white shadow-button hover:shadow-button-hover hover:translate-y-0.5 active:shadow-button-active active:translate-y-1 focus:ring-primary-500',
      secondary: 'bg-cream-100 text-brown-900 hover:bg-cream-200 focus:ring-primary-500',
      outline: 'border-2 border-primary-600 text-primary-700 bg-transparent hover:bg-primary-50 focus:ring-primary-500',
      ghost: 'text-primary-700 hover:bg-primary-50 focus:ring-primary-500',
    }

    const sizes = {
      sm: 'px-4 py-2 text-sm min-h-9',
      md: 'px-6 py-3 text-base min-h-11',
      lg: 'px-8 py-4 text-lg min-h-14',
    }

    return (
      <button
        ref={ref}
        className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <svg
              className="animate-spin-slow -ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="32"
                strokeDashoffset="12"
              />
            </svg>
            Loading...
          </>
        ) : (
          children
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'
